import { FsOptions, fsService, QueryConstraints } from '@services/firebase/firestore/firestore';
import { distinctStrArr } from '@utils/vanillaJs';
import {
  DocumentData,
  FirestoreDataConverter,
  PartialWithFieldValue,
  QueryConstraint,
  QueryNonFilterConstraint,
  UpdateData,
  WithFieldValue
} from 'firebase/firestore';

type SetDocInput<T extends DocumentData> = Partial<T> | PartialWithFieldValue<T>;

export const asSetDocData = <T extends DocumentData>(data: Partial<T>): PartialWithFieldValue<T> =>
  data as PartialWithFieldValue<T>;

export const asAddDocData = <T extends DocumentData>(data: T): WithFieldValue<T> =>
  data as WithFieldValue<T>;

export const asUpdateDocData = <T extends DocumentData, TInput extends Partial<T> = Partial<T>>(
  data: TInput
): UpdateData<T> => data as UpdateData<T>;

export class FirestoreCollectionHandler<DbModelType extends DocumentData> {
  private collectionName: string;

  constructor(collectionName: string) {
    this.collectionName = collectionName;
  }

  public doc<AppModelType = DbModelType>(
    docId: string,
    dataConverter?: FirestoreDataConverter<AppModelType, DbModelType>
  ) {
    return fsService.doc<DbModelType, AppModelType>(this.collectionName, docId, dataConverter);
  }

  public collection<AppModelType = DbModelType>(
    dataConverter?: FirestoreDataConverter<AppModelType, DbModelType>
  ) {
    return fsService.collection<DbModelType, AppModelType>(this.collectionName, dataConverter);
  }

  // Implementation
  public getDoc<AppModelType = DbModelType>(
    id: string,
    options?: {
      forceServer?: boolean;
      dataConverter?: FirestoreDataConverter<AppModelType, DbModelType>;
    }
  ): Promise<AppModelType> {
    return fsService.getDoc<DbModelType, AppModelType>(
      this.collectionName,
      id,
      options
    ) as Promise<AppModelType>;
  }

  public addDoc<AppModelType = DbModelType>(id: string, data: WithFieldValue<AppModelType>) {
    return fsService.addDoc<DbModelType, AppModelType>(this.collectionName, id, data);
  }

  public setDoc<AppModelType extends DocumentData = DbModelType>(
    id: string,
    data: SetDocInput<AppModelType>,
    options?: { merge?: boolean }
  ) {
    return fsService.setDoc<DbModelType, AppModelType>(
      this.collectionName,
      id,
      data as PartialWithFieldValue<AppModelType>,
      options
    );
  }

  public updateDoc<AppModelType extends DocumentData = DbModelType>(
    id: string,
    data: UpdateData<AppModelType>
  ) {
    return fsService.updateDoc<DbModelType, AppModelType>(this.collectionName, id, data);
  }

  public getCount<AppModelType = DbModelType>(
    queryConstraints:
      | QueryConstraint
      | QueryNonFilterConstraint
      | (QueryConstraint | QueryNonFilterConstraint)[] = [],
    options?: FsOptions<AppModelType>
  ) {
    return fsService.getCount<DbModelType, AppModelType>(
      this.collectionName,
      queryConstraints,
      options
    );
  }

  public deleteDoc(id: string) {
    return fsService.deleteDoc<DbModelType>(this.collectionName, id);
  }

  public getCountInBatches(
    [fieldPath, opStr, value]: [string, 'in' | 'array-contains-any', string[]],
    queryConstraints: QueryConstraint | QueryConstraint[] = [],
    options?: FsOptions<DbModelType> & {
      batchSize?: number;
    }
  ) {
    value = distinctStrArr(value);

    return fsService.getCountInBatches<DbModelType>(
      this.collectionName,
      [fieldPath, opStr, value],
      queryConstraints,
      options
    );
  }

  // Implementation of the overloaded method
  public getDocs<AppModelType extends DocumentData = DbModelType>(
    queryConstraints?: QueryConstraints,
    options?: FsOptions<AppModelType>
  ): Promise<AppModelType[]> {
    queryConstraints = queryConstraints || [];
    // Logic to either return snapshots or document arrays
    return fsService.getDocs<DbModelType, AppModelType>(
      this.collectionName,
      queryConstraints,
      options
    ) as Promise<AppModelType[]>;
  }

  // Define all overload signatures
  // @ts-ignore
  // Implementation of the overloaded method
  public getDocsInBatches<AppModelType extends DocumentData = DbModelType>(
    [fieldPath, opStr, value]: [string, 'in' | 'array-contains-any', string[]],
    queryConstraints?: QueryConstraints,
    updateStoreData?: (data: AppModelType[]) => void,
    options?: FsOptions<AppModelType> & {
      batchSize?: number;
    }
  ): Promise<AppModelType[][]> {
    // Logic to either return snapshots or document arrays
    value = distinctStrArr(value);
    return fsService.getDocsInBatches<DbModelType, AppModelType>(
      this.collectionName,
      [fieldPath, opStr, value],
      queryConstraints,
      updateStoreData,
      options
    ) as Promise<AppModelType[][]>;
  }
}

/**
 * Handler for a sub collection (e.g. entities/{id}/resource-grants).
 * Used directly it queries across all parents (collection group);
 * use `forParent(parentId)` for parent scoped operations.
 */
export class FirestoreSubCollectionHandler<
  DbModelType extends DocumentData
> extends FirestoreCollectionHandler<DbModelType> {
  constructor(
    private subCollectionName: string,
    private parentCollectionName: string
  ) {
    super(subCollectionName);
  }

  public forParent(parentId: string) {
    return new FirestoreCollectionHandler<DbModelType>(
      `${this.parentCollectionName}/${parentId}/${this.subCollectionName}`
    );
  }

  public override getDocs<AppModelType extends DocumentData = DbModelType>(
    queryConstraints?: QueryConstraints,
    options?: FsOptions<AppModelType>
  ): Promise<AppModelType[]> {
    return super.getDocs<AppModelType>(queryConstraints, { isGroupCollection: true, ...options });
  }

  public override getCount<AppModelType = DbModelType>(
    queryConstraints:
      | QueryConstraint
      | QueryNonFilterConstraint
      | (QueryConstraint | QueryNonFilterConstraint)[] = [],
    options?: FsOptions<AppModelType>
  ) {
    return super.getCount<AppModelType>(queryConstraints, {
      isGroupCollection: true,
      ...options
    });
  }
}
